import React from 'react';
import { useReleaseNote } from './Announcements';
import { dayLong } from '../lib/when';

/*
  What changed in OnHand, at the top of Home.

  **Built 10 September 2026 for job 25**, to Group H's approved drawings
  `note-admin`, `note-manager` and `note-rep`. **It is the owner's own
  instruction** and it changed three screens he had already signed off, which is
  why it is drawn on all three: Group C's Homes were approved on 31 August 2026.

  **Blue rather than gold, because nothing is wrong.** It is a thing you are
  being told. It sits at the TOP of Home, above the greeting, because a notice
  below the fold is a notice nobody reads, and it is the only thing on any Home
  screen that can be dismissed.

  **The body is written as lines and drawn as a list.** The database holds it as
  one piece of text, so what a person publishing a note types on separate lines
  comes out as separate points. A paragraph gets skipped; three short lines get
  read, which is what the owner asked for when he said one button and it goes
  away rather than something to study.
*/
export default function ReleaseNote() {
  const { note, busy, dismiss } = useReleaseNote();
  if (!note) return null;

  const lines = String(note.body || '')
    .split('\n')
    .map(l => l.replace(/^\s*[-*•]\s*/, '').trim())
    .filter(Boolean);

  return (
    <div className="rel-note">
      <div className="rel-note-h">
        <span className="rel-note-i">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true"
            stroke="var(--on-action)" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="9" /><path d="M12 11v5.5" /><path d="M12 7.6h.01" />
          </svg>
        </span>
        <div style={{ minWidth: 0 }}>
          <div className="rel-note-t">{note.title}</div>
          <div className="rel-note-v">
            Version {note.version}
            {note.published_at ? ' - ' + dayLong(note.published_at) : ''}
          </div>
        </div>
      </div>
      {lines.length > 0 && (
        <ul className="rel-note-l">
          {lines.map((l, i) => <li key={i}>{l}</li>)}
        </ul>
      )}
      <div className="rel-note-f">
        <button type="button" className="rel-note-a" disabled={busy} onClick={dismiss}>
          {busy ? 'Saving...' : 'I acknowledge'}
        </button>
      </div>
    </div>
  );
}

/*
  **The date is written by the app's one date writer**, `dayLong` in
  `src/lib/when.js`, which gives "5 September 2026" and is what Group H's
  drawing shows.

  **The first version of this file wrote its own**, and it was wrong in the way
  `when.js` exists to stop: an administrator can choose the site's time zone in
  Site Settings, and a date formatted with the browser's own zone reads off
  whatever machine the person happens to be sitting at. Two people could have
  read two different days off the same note. OnHand had that fault in 54 places
  until 5 September 2026 and this would have made it 55.

  **And `published_at` is safe to read as a moment**, which is worth saying
  because the rule on this project is the opposite. A plain day like 2026-09-18
  is read by a browser as midnight in Greenwich, which in Texas is the evening
  BEFORE, so plain days are built out of their three numbers instead. This one
  carries a time and a zone, so there is nothing to guess.
*/
